import React, { useEffect, useState } from 'react'
import { clearAllAppData } from '../utils/cacheUtils'

interface LoadingTimeoutDetectorProps {
  children: React.ReactNode
}

export const LoadingTimeoutDetector: React.FC<LoadingTimeoutDetectorProps> = ({ children }) => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false)

  useEffect(() => {
    // Check if we're on CloudFlare Pages or the production domain
    const hostname = window.location.hostname
    const isCloudFlareDeployment = hostname.includes('.pages.dev') || hostname.includes('sergio.app')

    if (!isCloudFlareDeployment) return

    // Don't trigger timeout on routes page as it interferes with map initialization
    const pathname = window.location.pathname
    if (pathname === '/routing' || pathname === '/routes') {
      console.log('🗺️ Skipping loading timeout on routes page to allow map initialization')
      return
    }

    console.log('⏰ Loading timeout detector started - will check in 60 seconds')

    // Set a more generous timeout to avoid false positives during auth
    const timeoutId = setTimeout(() => {
      // Check if the app seems to be stuck loading
      const hasLoadedContent = document.querySelector('[data-app-loaded]') !== null ||
                              document.querySelector('.space-y-6') !== null || // Main app content
                              document.querySelector('nav') !== null || // Navigation loaded
                              document.querySelectorAll('.animate-spin').length === 0 // No spinners

      const hasActiveSpinners = document.querySelectorAll('.animate-spin').length > 0
      const hasAuthContent = document.querySelector('[data-auth-provider]') !== null

      console.log('🔍 Loading timeout check:', {
        hasLoadedContent,
        hasActiveSpinners,
        hasAuthContent,
        spinnerCount: document.querySelectorAll('.animate-spin').length,
        pathname: window.location.pathname
      })

      // Only show timeout if we genuinely seem stuck
      if (!hasLoadedContent && hasActiveSpinners && !hasAuthContent) {
        console.warn('🕐 Loading timeout detected - app seems genuinely stuck')
        setShowTimeoutWarning(true)
      } else {
        console.log('✅ Loading timeout check passed - app seems to be working')
      }
    }, 60000) // Increased to 60 seconds to give auth more time

    return () => clearTimeout(timeoutId)
  }, [])

  const handleForceRefresh = () => {
    console.log('🚀 Loading timeout - Force refresh requested')
    clearAllAppData()

    // Add cache busting parameter and force refresh
    const url = new URL(window.location.href)
    url.searchParams.set('_cacheBust', Date.now().toString())
    url.searchParams.set('_loadingTimeout', 'true')
    window.location.replace(url.toString())
  }

  const handleDismiss = () => {
    setShowTimeoutWarning(false)
  }

  if (showTimeoutWarning) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-muted px-4">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-[var(--color-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-strong">
                Loading Taking Too Long
              </h3>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-muted">
              The application has been loading for over 60 seconds. This usually happens after a CloudFlare deployment when cached files become stale.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleForceRefresh}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Clear Cache & Force Refresh
            </button>

            <button
              onClick={handleDismiss}
              className="w-full border border-subtle bg-surface hover:bg-surface-hover text-muted font-medium py-2 px-4 rounded transition-colors"
            >
              Continue Waiting
            </button>
          </div>

          <div
            className="mt-4 p-3 border rounded"
            style={{
              borderColor: 'color-mix(in srgb, var(--color-accent) 35%, transparent)',
              backgroundColor: 'color-mix(in srgb, var(--color-accent) 10%, var(--color-surface))'
            }}
          >
            <p className="text-xs text-subtle">
              <strong>Why does this happen?</strong> CloudFlare deployments can cause browser caches to become stale, leading to infinite loading loops. Clearing the cache usually fixes this.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
